import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarCheck, Clock, ArrowRight, ArrowLeft, Check, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnBookingSection() {
  const [step, setStep] = useState(1); // 1=calendar, 2=time, 3=form, 4=success
  const [availableSlots, setAvailableSlots] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", pflegegrad: "", anliegen: "", beratungsart: "video" });
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchSlots(currentMonth.getMonth() + 1, currentMonth.getFullYear());
  }, [currentMonth]);

  const fetchSlots = async (month, year) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/appointments/available-slots?month=${month}&year=${year}`);
      setAvailableSlots(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const availableDates = Object.keys(availableSlots).map(d => new Date(d + "T00:00:00"));

  const handleDateSelect = (date) => {
    if (!date) return;
    const dateStr = date.toLocaleDateString("sv-SE"); // YYYY-MM-DD
    if (availableSlots[dateStr]) {
      setSelectedDate(dateStr);
      setSelectedTime(null);
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments/book`, {
        date: selectedDate,
        time_slot: selectedTime,
        ...form,
      });
      setStep(4);
      toast.success("Termin erfolgreich gebucht!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Buchung.");
    }
    setSubmitting(false);
  };

  const isDayAvailable = (day) => {
    const dateStr = day.toLocaleDateString("sv-SE");
    return !!availableSlots[dateStr];
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <section id="termin-buchen" data-testid="booking-section" className="py-20 lg:py-28 bg-[#F0F9FF] scroll-mt-[120px] xl:scroll-mt-[96px]" aria-label="Online Termin buchen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 bg-white px-4 py-1.5 rounded-full mb-4 shadow-sm">
            <CalendarCheck className="h-4 w-4" />
            Online Terminbuchung
          </span>
          <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Jetzt Termin buchen
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            Wählen Sie einen passenden Termin für Ihre persönliche Beratung — per Video, Telefon oder vor Ort. Die Buchung dauert nur wenige Minuten.
          </p>
        </div>

        {/* Urgency */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
            <Clock className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Begrenzte Termine pro Woche — jetzt freien Platz sichern</span>
          </div>
        </div>

        {/* Booking widget */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl shadow-cyan-900/5 overflow-hidden border border-slate-200/60" data-testid="booking-widget">
            {/* Progress steps */}
            <div className="flex border-b border-slate-100 bg-slate-50/50">
              {[
                { num: 1, label: "Datum" },
                { num: 2, label: "Uhrzeit" },
                { num: 3, label: "Daten" },
                { num: 4, label: "Bestätigt" },
              ].map((s) => (
                <div key={s.num} className={`flex-1 flex items-center justify-center gap-2 py-4 text-sm font-medium transition-colors ${step >= s.num ? 'text-cyan-700' : 'text-slate-400'}`}>
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${step > s.num ? 'bg-green-100 text-green-700' : step === s.num ? 'bg-cyan-700 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    {step > s.num ? <Check className="h-3.5 w-3.5" /> : s.num}
                  </div>
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="p-6 sm:p-8">
              {/* Step 1: Calendar */}
              {step === 1 && (
                <div data-testid="booking-step-1" className="flex flex-col items-center">
                  <p className="text-base text-slate-600 mb-6 text-center">Wählen Sie einen Tag mit freien Terminen:</p>
                  <Calendar
                    mode="single"
                    selected={selectedDate ? new Date(selectedDate + "T00:00:00") : undefined}
                    onSelect={handleDateSelect}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    disabled={(day) => !isDayAvailable(day)}
                    className="rounded-xl border border-slate-200"
                    classNames={{
                      day_disabled: "text-slate-300 opacity-50",
                      day_selected: "bg-cyan-700 text-white hover:bg-cyan-700 hover:text-white focus:bg-cyan-700 focus:text-white",
                      day_today: "bg-cyan-50 text-cyan-700 font-bold",
                    }}
                  />
                  {loading && <p className="text-sm text-slate-400 mt-3">Verfügbarkeit wird geladen...</p>}
                  <p className="text-xs text-slate-400 mt-4">Termine verfügbar: Mo–Fr um 14:00, 15:00 oder 16:00 Uhr</p>
                </div>
              )}

              {/* Step 2: Time slot */}
              {step === 2 && selectedDate && (
                <div data-testid="booking-step-2">
                  <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 mb-4 transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Datum ändern
                  </button>
                  <p className="text-base text-slate-600 mb-2">Gewählter Tag:</p>
                  <p className="text-lg font-semibold text-slate-800 mb-6 font-['Manrope']">{formatDate(selectedDate)}</p>
                  <p className="text-base text-slate-600 mb-4">Wählen Sie eine Uhrzeit:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {(availableSlots[selectedDate] || []).map((slot) => (
                      <button
                        key={slot}
                        data-testid={`slot-${slot}`}
                        onClick={() => { setSelectedTime(slot); setStep(3); }}
                        className={`p-4 rounded-xl border-2 text-center transition-all hover:shadow-md ${
                          selectedTime === slot ? 'border-cyan-700 bg-cyan-50 text-cyan-700' : 'border-slate-200 hover:border-cyan-300 text-slate-700'
                        }`}
                      >
                        <Clock className="h-5 w-5 mx-auto mb-1 opacity-60" />
                        <span className="text-lg font-semibold">{slot} Uhr</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Form */}
              {step === 3 && (
                <div data-testid="booking-step-3">
                  <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 mb-4 transition-colors">
                    <ArrowLeft className="h-4 w-4" /> Uhrzeit ändern
                  </button>
                  <div className="bg-cyan-50/50 rounded-xl p-4 mb-6 border border-cyan-100">
                    <p className="text-sm font-medium text-cyan-800">
                      Ihr Termin: {formatDate(selectedDate)} um {selectedTime} Uhr
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Beratungsart */}
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2.5 block">Wie möchten Sie beraten werden?</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          data-testid="booking-type-video"
                          onClick={() => setForm({...form, beratungsart: "video"})}
                          className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${
                            form.beratungsart === "video" ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200" : "border-slate-200 hover:border-cyan-200"
                          }`}
                        >
                          <svg className="h-7 w-7 mx-auto mb-2 text-cyan-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" /></svg>
                          <p className="text-sm font-bold text-slate-800">Video</p>
                          <p className="text-xs text-slate-500 mt-1">Per Bildschirm</p>
                        </button>
                        <button
                          type="button"
                          data-testid="booking-type-phone"
                          onClick={() => setForm({...form, beratungsart: "telefon"})}
                          className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${
                            form.beratungsart === "telefon" ? "border-green-600 bg-green-50 ring-1 ring-green-200" : "border-slate-200 hover:border-green-200"
                          }`}
                        >
                          <svg className="h-7 w-7 mx-auto mb-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" /></svg>
                          <p className="text-sm font-bold text-slate-800">Telefon</p>
                          <p className="text-xs text-slate-500 mt-1">Wir rufen an</p>
                        </button>
                        <button
                          type="button"
                          data-testid="booking-type-praesenz"
                          onClick={() => setForm({...form, beratungsart: "praesenz"})}
                          className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${
                            form.beratungsart === "praesenz" ? "border-amber-600 bg-amber-50 ring-1 ring-amber-200" : "border-slate-200 hover:border-amber-200"
                          }`}
                        >
                          <svg className="h-7 w-7 mx-auto mb-2 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                          <p className="text-sm font-bold text-slate-800">Vor Ort</p>
                          <p className="text-xs text-slate-500 mt-1">Bei Ihnen zuhause</p>
                        </button>
                      </div>
                      {/* Empfehlung Präsenztermin */}
                      <div className="flex items-start gap-2.5 mt-3 p-3.5 bg-amber-50/70 border border-amber-200/60 rounded-xl" data-testid="praesenz-hinweis">
                        <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800 leading-relaxed">
                          <span className="font-semibold">Empfehlung:</span> Bei einer Digitalberatung wird ein erstmaliger Pr&auml;senztermin vor Ort stark empfohlen, um Ihre Pflegesituation pers&ouml;nlich einzusch&auml;tzen. Folgeberatungen k&ouml;nnen bequem per Video oder Telefon erfolgen.
                        </p>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Name *</Label>
                        <Input data-testid="booking-name" placeholder="Ihr vollständiger Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="h-12 rounded-xl border-slate-200 bg-white text-base" />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Telefon *</Label>
                        <Input data-testid="booking-phone" type="tel" placeholder="0176 ..." value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="h-12 rounded-xl border-slate-200 bg-white text-base" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-Mail *</Label>
                      <Input data-testid="booking-email" type="email" placeholder="ihre@email.de" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="h-12 rounded-xl border-slate-200 bg-white text-base" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Pflegegrad</Label>
                        <select data-testid="booking-pflegegrad" value={form.pflegegrad} onChange={(e) => setForm({...form, pflegegrad: e.target.value})} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base">
                          <option value="">Bitte wählen</option>
                          <option value="1">Pflegegrad 1</option>
                          <option value="2">Pflegegrad 2</option>
                          <option value="3">Pflegegrad 3</option>
                          <option value="4">Pflegegrad 4</option>
                          <option value="5">Pflegegrad 5</option>
                          <option value="beantragt">Beantragt</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Anliegen</Label>
                        <select data-testid="booking-anliegen" value={form.anliegen} onChange={(e) => setForm({...form, anliegen: e.target.value})} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-base">
                          <option value="">Bitte wählen</option>
                          <option value="erstberatung">Erstberatung</option>
                          <option value="folgeberatung">Folgeberatung</option>
                          <option value="entlastungsbetrag">Entlastungsbetrag</option>
                          <option value="sonstiges">Sonstiges</option>
                        </select>
                      </div>
                    </div>
                    <Button type="submit" data-testid="booking-submit-btn" disabled={submitting} className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold shadow-sm hover:shadow-md transition-all mt-2">
                      {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gebucht...</> : <>Termin verbindlich buchen <ArrowRight className="h-4 w-4 ml-2" /></>}
                    </Button>
                  </form>
                </div>
              )}

              {/* Step 4: Success */}
              {step === 4 && (
                <div data-testid="booking-step-4" className="text-center py-8">
                  <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-3">Termin gebucht!</h3>
                  <p className="text-lg text-slate-600 mb-2">
                    Ihr {form.beratungsart === "telefon" ? "Telefon" : form.beratungsart === "praesenz" ? "Pr\u00e4senz" : "Video"}-Beratungstermin am <strong>{selectedDate && formatDate(selectedDate)}</strong> um <strong>{selectedTime} Uhr</strong> ist best&auml;tigt.
                  </p>
                  <p className="text-base text-slate-500 mb-6">
                    {form.beratungsart === "telefon"
                      ? "Wir rufen Sie zum vereinbarten Zeitpunkt an. Bitte halten Sie Ihr Telefon bereit."
                      : form.beratungsart === "praesenz"
                      ? "Unser Berater kommt zum vereinbarten Termin zu Ihnen nach Hause. Wir melden uns vorher telefonisch bei Ihnen."
                      : "Sie erhalten in K\u00fcrze eine Best\u00e4tigung mit dem Video-Link."}
                  </p>

                  {/* Portal hint */}
                  <div className="bg-cyan-50 border border-cyan-100 rounded-xl p-5 mb-6 text-left max-w-sm mx-auto" data-testid="portal-hint">
                    <p className="text-sm font-semibold text-cyan-800 mb-2">Ihr persönlicher Bereich</p>
                    <p className="text-sm text-cyan-700 mb-3">
                      Unter &bdquo;Mein Bereich&ldquo; finden Sie Ihren Termin{form.beratungsart === "video" ? ", den Video-Link" : ""} und nach der Beratung Ihren Beratungsbogen als PDF.
                    </p>
                    <a
                      href="/portal/login"
                      data-testid="portal-link"
                      className="inline-flex items-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-5 py-2.5 text-sm font-semibold transition-all active:scale-[0.98]"
                    >
                      Zu „Mein Bereich"
                      <ArrowRight className="h-4 w-4" />
                    </a>
                    <p className="text-xs text-cyan-600 mt-3">
                      Anmeldung mit Ihrer E-Mail und Telefonnummer.
                    </p>
                  </div>

                  <Button onClick={() => { setStep(1); setSelectedDate(null); setSelectedTime(null); setForm({name:"",email:"",phone:"",pflegegrad:"",anliegen:"",beratungsart:"video"}); }} variant="outline" className="rounded-xl px-6 h-12 text-base">
                    Weiteren Termin buchen
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Info below */}
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { text: "Kostenlos mit Pflegegrad", color: "text-green-600", bg: "bg-green-50" },
              { text: "Datenschutz-konform", color: "text-cyan-600", bg: "bg-cyan-50" },
              { text: "Bestätigung per E-Mail", color: "text-green-600", bg: "bg-green-50" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/80 rounded-xl p-4 border border-slate-100">
                <div className={`h-9 w-9 ${item.bg} rounded-lg flex items-center justify-center shrink-0`}>
                  <Check className={`h-5 w-5 ${item.color}`} />
                </div>
                <span className="text-sm font-medium text-slate-600">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
